import type { StackProps } from "@chakra-ui/react"
import {
  Box,
  Button,
  ButtonProps,
  HStack,
  Heading,
  IconButton,
  Menu,
  Portal,
  Separator,
  Text,
  VStack
} from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import { isPrimaryUnmodifiedClick } from "@pathwright/utils/src/event/click"
import { withUrlHash } from "@pathwright/utils/src/url/hash"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import path from "path-browserify"
import {
  KeyboardEvent,
  MouseEvent,
  MouseEventHandler,
  PointerEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useRouteMatch } from "react-router-dom"
import useDoubleActivate from "../../hooks/useDoubleActivate"
import { exhaustiveGuard } from "../../utils/typing"
import ItemSelector, {
  ItemSelectorOption,
  ItemSelectorProps
} from "../item-selector/ItemSelector"
import Link, { LinkProps, LinkRelativeTo, LinkTo } from "../link/Link"
import { resolveLinkPropsWithHash, withHashTo } from "../link/hashNavigation"
import useTranslate from "../lng/useTranslate"
import SortableList, {
  SortableListChangeArgs,
  SortableListDragHandleProps
} from "../sortable/SortableList"
import RevealOnGroupHover from "../style/RevealOnGroupHover"
import { AugmentationPanelRow } from "./AugmentationPanelRow"
import {
  AUGMENTATION_PANEL_ADD_CONTROL_TOUCH_CSS,
  augmentationPanelAddSelectorChakraStyles
} from "./augmentationPanelSelectorStyles"
import {
  AUGMENTATION_PANEL_MENU_CONTENT_PROPS,
  AUGMENTATION_PANEL_MENU_ITEM_ICON_CSS,
  AUGMENTATION_PANEL_OPTIONS_MENU_ITEM_CSS,
  AUGMENTATION_PANEL_OPTIONS_MENU_ITEM_GAP,
  AUGMENTATION_PANEL_SELECT_PROPS,
  AUGMENTATION_PANEL_TYPOGRAPHY_CSS
} from "./augmentationPanelStyles"
import {
  dispatchAugmentationPanelHashNavigation,
  useAugmentationPanelHashClickHandler,
  useSetHashKey
} from "./hashNavigation"
import {
  AUGMENTATION_SECTION_FOCUS_CLASS,
  AUGMENTATION_SECTION_FOCUS_IN_MS,
  AUGMENTATION_SECTION_FOCUS_OUT_MS
} from "./sectionFocus"
import { useAugmentationPanelAddRequest } from "./panelAddRequest"
import { AUGMENTATION_PANEL_ADD_FORM_CLASS } from "./useAugmentationPanelEscape"

export const AugmentationPanelSectionGroup = ({
  children,
  gap = 0
}: {
  children: ReactNode
  gap?: StackProps["gap"]
}) => {
  return (
    <VStack
      align="stretch"
      className="AugmentationPanelSectionGroup"
      w="full"
      gap={gap}
    >
      {children}
    </VStack>
  )
}

/** Filled state shared by hover, press, drag, and the focus flash. */
const ITEM_FILL_BG = "augmentationPanel.fill.muted"

// When a section is briefly focused (e.g. after scrolling to it), flash each
// list item into its hover fill with a hairline border. The page's cards flash a
// drop shadow instead — on rows this flat one reads as a blurry halo.
const SECTION_FOCUS_CSS = {
  [`&.${AUGMENTATION_SECTION_FOCUS_CLASS} .AugmentationPanelSectionItem`]: {
    bg: ITEM_FILL_BG,
    // Inset so the row can't shift by the border's width mid-flash.
    boxShadow: "augmentationPanelSectionFocus",
    transition: `background-color ${AUGMENTATION_SECTION_FOCUS_IN_MS}ms ease, box-shadow ${AUGMENTATION_SECTION_FOCUS_IN_MS}ms ease`
  }
} as const

export const AugmentationPanelSectionRoot = ({
  children,
  css,
  id
}: {
  children: ReactNode
  css?: StackProps["css"]
  id?: string
}) => {
  return (
    <VStack
      align="stretch"
      as="section"
      borderTopColor="augmentationPanel.border"
      borderTopWidth="1px"
      className="AugmentationPanelSection"
      css={css ? [SECTION_FOCUS_CSS, css] : SECTION_FOCUS_CSS}
      gap="augmentationPanel"
      id={id}
      pb="augmentationPanel"
      position="relative"
      pt="augmentationPanel"
      w="full"
    >
      {children}
    </VStack>
  )
}

export const AugmentationPanelSectionContent = ({
  children,
  gap = 0
}: {
  children: ReactNode
  gap?: StackProps["gap"]
}) => {
  return (
    <VStack
      align="stretch"
      className="AugmentationPanelSectionContent"
      gap={gap}
      w="full"
      // This bit of padding allows focus outlines to show around interactive elements (buttons, dropdowns, etc.)
      px={1}
    >
      {children}
    </VStack>
  )
}

/** @deprecated Use AugmentationPanelSection.Root and Content. */
const AugmentationPanelSectionContainer = ({
  children
}: {
  children: ReactNode
}) => (
  <VStack
    className="AugmentationPanelSection"
    w="full"
    gap={2}
    px={2}
    position="relative"
  >
    {children}
  </VStack>
)

/** @deprecated Section boundaries are rendered by AugmentationPanelSection.Root. */
const AugmentationPanelSectionDivider = () => (
  <Separator w="full" mt={2} mb={4} h="1px" border="none" bg="blackAlpha.200" />
)

export const AugmentationPanelSectionHeading = ({
  children,
  linkProps,
  hashKey
}: {
  children: ReactNode
  linkProps?: LinkProps
  hashKey?: string
}) => {
  const { url } = useRouteMatch()
  const setHashKey = useSetHashKey()

  const linkWithHashProps = useMemo(
    () => resolveLinkPropsWithHash(linkProps, hashKey, url),
    [hashKey, linkProps, url]
  )

  const handleHashNavigation = useAugmentationPanelHashClickHandler(hashKey)

  // The control fills the heading rather than the other way around, so screen
  // readers announce a heading that contains a link or button.
  const controlProps = {
    alignItems: "center",
    color: "inherit",
    display: "inline-flex",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
    h: "full",
    justifyContent: "flex-start",
    textDecoration: "none",
    w: "full",
    _hover: { textDecoration: "none" }
  } as const

  return (
    <Heading
      as="h2"
      className="AugmentationPanelSectionHeading"
      // The panel's text style, one weight up: a section heading, not a label.
      textStyle="augmentationPanel"
      fontWeight={600}
      h="17px"
      m={0}
      textAlign="left"
      w="full"
    >
      {linkWithHashProps ? (
        <Link
          {...linkWithHashProps}
          {...controlProps}
          variant="plain"
          onClick={(event) => {
            handleHashNavigation(event)
            linkWithHashProps.onClick?.(event)
          }}
        >
          {children}
        </Link>
      ) : hashKey ? (
        <Button
          {...controlProps}
          variant="plain"
          minH={0}
          p={0}
          onClick={() => {
            setHashKey(hashKey)
          }}
        >
          {children}
        </Button>
      ) : (
        children
      )}
    </Heading>
  )
}

type AugmentationPanelItemControlType =
  | "edit"
  | "delete"
  | "move-up"
  | "move-down"

export type AugmentationPanelItemControlBase = {
  type: AugmentationPanelItemControlType
  hashKey?: string
}

export type AugmentationPanelItemEditControl =
  AugmentationPanelItemControlBase &
    AugmentationPanelItemLinkTarget & {
      type: "edit"
      onClick?: () => void
    }

export type AugmentationPanelItemMoveUpControl =
  AugmentationPanelItemControlBase & {
    type: "move-up"
    handler: () => void | Promise<void>
  }

export type AugmentationPanelItemMoveDownControl =
  AugmentationPanelItemControlBase & {
    type: "move-down"
    handler: () => void | Promise<void>
  }

export type AugmentationPanelItemDeleteControl =
  AugmentationPanelItemControlBase & {
    type: "delete"
    handler: () => void | Promise<void>
    loading?: boolean
    disabled?: boolean
  }

export type AugmentationPanelItemControl =
  | AugmentationPanelItemEditControl
  | AugmentationPanelItemDeleteControl
  | AugmentationPanelItemMoveUpControl
  | AugmentationPanelItemMoveDownControl

/**
 * Link target for a panel item label or trailing edit control. Three props cover
 * three navigation contexts (only one should be set; resolution order is
 * `to` → `panelRoute` → `href`):
 *
 * - `to` — Main app route via react-router `Link` (pathname, location object,
 *   etc.). Use for normal in-app pages (e.g. `/manage/school/library/author/1/`).
 *
 * - `panelRoute` — Relative segment under the *current augmentation panel* route
 *   (joined with `useRouteMatch().url`, e.g. `668` → `/cover/668` when the
 *   panel is at `/cover`). Use when drilling into nested panel views without
 *   hard-coding the `?panel=` prefix or parent path.
 *
 * - `href` — Direct URL on `Link` `href` (full navigation, not panel-relative).
 *   Use for external sites or when the target is outside react-router/panel
 *   routing. Prefer `to` for in-app targets.
 */
export type AugmentationPanelItemLinkTarget = {
  to?: LinkTo
  relativeTo?: LinkRelativeTo
  href?: string
  panelRoute?: string
  hashKey?: string
}

export type AugmentationPanelItemProps = {
  itemKey: string
  label: string
  data?: any
  // Whether any item in the list is being dragged.
  isPerformingDrag?: boolean
} & AugmentationPanelItemLinkTarget & {
    onClick?: () => void
    /** Supplemental mouse double-click/touch double-tap action for neutral row space. */
    onDoubleActivate?: () => void
    icon?: IconName
    /** Replaces the icon slot. */
    leading?: ReactNode
    controls?: AugmentationPanelItemControl[]
    /** Renders this row as a compact inline editor. */
    editState?: {
      leading?: ReactNode
      content: ReactNode
      onSave: () => void
      onCancel: () => void
      canSave?: boolean
      isSaving?: boolean
    }
    /** When false, this item remains fixed when the list is sortable. */
    sortable?: boolean
    tone?: "muted"
  }

export type AugmentationPanelSectionListSortProps = {
  onSort: (
    change: SortableListChangeArgs<AugmentationPanelItemProps>
  ) => void | Promise<void>
  onMove: (
    moveItem: AugmentationPanelItemProps,
    movedIndex: number
  ) => void | Promise<void>
  disabled?: boolean
  animateLayout?: boolean
}

export type AugmentationPanelSectionNavigateCreateProps = {
  /** Child panel route segment (joined with the current route match url). */
  panelRoute?: string
  getTo?: (inputValue: string) => LinkTo
}

export type AugmentationPanelSectionAddSharedProps = {
  itemKey: string
  label: string
  hashKey?: string
}

export type AugmentationPanelSectionAddSelectProps<
  T extends ItemSelectorOption = ItemSelectorOption
> = Omit<
  ItemSelectorProps<T>,
  "onChange" | "value" | "getNavigateCreateTo" | "renderControlRightElement"
> & {
  onSubmit: (option: T) => void | Promise<void>
  getErrorMessage?: (error: unknown) => string
  /** Create menu row navigates to a child panel instead of inline create input. */
  navigateCreate?: AugmentationPanelSectionNavigateCreateProps
}

export type AugmentationPanelSectionAddLinkProps =
  AugmentationPanelItemLinkTarget

export type AugmentationPanelSectionAddProps<
  T extends ItemSelectorOption = ItemSelectorOption
> = AugmentationPanelSectionAddSharedProps &
  (
    | { select: AugmentationPanelSectionAddSelectProps<T> }
    | { link: AugmentationPanelSectionAddLinkProps }
    | { onClick: () => void }
  )

function AugmentationPanelSectionAddItem({
  itemKey,
  label,
  hashKey,
  onClick,
  to,
  relativeTo,
  href,
  panelRoute
}: Pick<
  AugmentationPanelItemProps,
  | "itemKey"
  | "label"
  | "hashKey"
  | "onClick"
  | "to"
  | "relativeTo"
  | "href"
  | "panelRoute"
>) {
  return (
    <AugmentationPanelSectionItem
      itemKey={itemKey}
      icon="plus"
      tone="muted"
      label={label}
      hashKey={hashKey}
      onClick={onClick}
      to={to}
      relativeTo={relativeTo}
      href={href}
      panelRoute={panelRoute}
    />
  )
}

function AugmentationPanelSectionAddLinkButton({
  itemKey,
  label,
  hashKey,
  link
}: AugmentationPanelSectionAddSharedProps & {
  link: AugmentationPanelSectionAddLinkProps
}) {
  return (
    <AugmentationPanelSectionAddItem
      itemKey={itemKey}
      label={label}
      hashKey={hashKey}
      {...link}
    />
  )
}

/**
 * Confirm and cancel for the inline add, rendered inside the selector's control
 * so they read as the pending row's own trailing controls.
 */
function AugmentationPanelSectionAddFormControls({
  itemLabel,
  canSubmit,
  isSubmitting,
  onSubmit,
  onCancel
}: {
  itemLabel: string
  canSubmit: boolean
  isSubmitting: boolean
  onSubmit: () => void
  onCancel: () => void
}) {
  const lowerItemLabel = itemLabel.toLowerCase()

  return (
    <HStack
      className="AugmentationPanelSectionAddFormControls"
      gap="2px"
      // Pointer events inside the control otherwise reach react-select, which
      // treats them as a click on the field and toggles the menu.
      onPointerDown={(event: PointerEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()
      }}
    >
      <IconButton
        aria-label={`Cancel adding ${lowerItemLabel}`}
        size="2xs"
        variant="ghost"
        rounded="full"
        color="augmentationPanel.text"
        opacity={0.6}
        disabled={isSubmitting}
        onClick={onCancel}
        _hover={{ bg: "augmentationPanel.fill.muted", opacity: 1 }}
        css={AUGMENTATION_PANEL_ADD_CONTROL_TOUCH_CSS}
      >
        <Pathicon icon="x" />
      </IconButton>
      {canSubmit ? (
        <IconButton
          aria-label={`Add ${lowerItemLabel}`}
          size="2xs"
          rounded="full"
          bg="augmentationPanel.fill.strong"
          color="augmentationPanel.text"
          loading={isSubmitting}
          onClick={onSubmit}
          _hover={{ bg: "augmentationPanel.fill.bold" }}
          css={AUGMENTATION_PANEL_ADD_CONTROL_TOUCH_CSS}
        >
          <Pathicon icon="check" />
        </IconButton>
      ) : null}
    </HStack>
  )
}

function AugmentationPanelSectionAddSelectButton<
  T extends ItemSelectorOption = ItemSelectorOption
>({
  itemKey,
  label,
  hashKey,
  select: {
    itemLabel,
    isLoading,
    getNewOptionData,
    onSubmit,
    getErrorMessage = defaultGetAddErrorMessage,
    navigateCreate,
    chakraStyles,
    itemInputProps,
    ...selectorProps
  }
}: AugmentationPanelSectionAddSharedProps & {
  select: AugmentationPanelSectionAddSelectProps<T>
}) {
  const { url } = useRouteMatch()
  const [isEditing, setIsEditing] = useState(false)
  const setHashKey = useSetHashKey()
  const [value, setValue] = useState<T | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const canSubmit = Boolean(value?.name.trim()) && !isSubmitting

  // A control outside the panel can open this picker directly.
  useAugmentationPanelAddRequest(itemKey, () => setIsEditing(true))

  const getNavigateCreateTo = useMemo(() => {
    if (
      !navigateCreate ||
      (!navigateCreate.getTo && !navigateCreate.panelRoute)
    )
      return undefined

    return (inputValue: string): LinkTo => {
      if (navigateCreate.getTo) {
        return withHashTo(navigateCreate.getTo(inputValue), hashKey)
      }

      const trimmed = inputValue.trim()
      const pathname = path.join(url, navigateCreate.panelRoute!)

      const to = trimmed
        ? {
            pathname,
            search: `?${new URLSearchParams({ name: trimmed }).toString()}`
          }
        : { pathname }

      return withHashTo(to, hashKey)
    }
  }, [hashKey, navigateCreate, url])

  // The panel is too narrow for "Select or create a <thing>" to fit, and the
  // section heading already names the thing.
  const placeholder =
    navigateCreate || selectorProps.mode !== "select"
      ? "Select or create..."
      : "Select..."

  async function handleSubmit() {
    if (!value?.name.trim()) return

    try {
      setIsSubmitting(true)
      setErrorMessage(null)
      await onSubmit(value)
      setValue(null)
      selectorProps.onInputValueChange?.("")
      setIsEditing(false)
      // Wait a frame to ensure the hash key doesn't get reset prematurely.
      requestAnimationFrame(() => {
        setHashKey(hashKey)
      })
    } catch (error) {
      setErrorMessage(getErrorMessage(error))
      // Keep the inline selector open so users can recover from submit failures.
    } finally {
      setIsSubmitting(false)
    }
  }

  // After the picker is cancelled its row comes back; keyboard focus goes
  // with it instead of falling to the page.
  const rowRef = useRef<HTMLDivElement>(null)
  const returnFocusToRow = useRef(false)
  useEffect(() => {
    if (isEditing || !returnFocusToRow.current) return
    returnFocusToRow.current = false
    rowRef.current?.querySelector<HTMLElement>("a, button")?.focus()
  }, [isEditing])

  function handleCancel() {
    // Only when focus is in the picker; a pointer cancel keeps its own focus.
    returnFocusToRow.current = !!document.activeElement?.closest(
      `.${AUGMENTATION_PANEL_ADD_FORM_CLASS}`
    )
    setValue(null)
    setErrorMessage(null)
    selectorProps.onInputValueChange?.("")
    setIsEditing(false)
  }

  if (!isEditing) {
    return (
      <Box ref={rowRef} display="contents">
        <AugmentationPanelSectionAddItem
          itemKey={itemKey}
          label={label}
          hashKey={hashKey}
          onClick={() => {
            setIsEditing(true)
            requestAnimationFrame(() => {
              setHashKey(hashKey)
            })
          }}
        />
      </Box>
    )
  }

  return (
    <VStack
      align="stretch"
      gap={2}
      w="full"
      className={AUGMENTATION_PANEL_ADD_FORM_CLASS}
    >
      <ItemSelector
        placeholder={placeholder}
        {...selectorProps}
        chakraStyles={{
          ...augmentationPanelAddSelectorChakraStyles<T>(),
          ...chakraStyles
        }}
        itemInputProps={{
          ...AUGMENTATION_PANEL_SELECT_PROPS,
          ...itemInputProps
        }}
        itemLabel={itemLabel}
        value={value}
        onChange={(option) => {
          setValue(option || null)
          setErrorMessage(null)
        }}
        isLoading={isLoading}
        getNewOptionData={getNewOptionData}
        getNavigateCreateTo={getNavigateCreateTo}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            // Escape cancels the whole add form, menu and all. Something claims
            // Escape in the capture phase before the selector sees it, so the
            // selector can't dismiss its own menu first — and left to bubble,
            // Escape closes the panel and the surface behind it.
            event.preventDefault()
            event.stopPropagation()
            handleCancel()
          } else if (event.key === "Enter" && !menuIsOpen && canSubmit) {
            // With the menu open, Enter picks the focused option.
            event.preventDefault()
            void handleSubmit()
          }
        }}
        renderControlRightElement={() => (
          <AugmentationPanelSectionAddFormControls
            itemLabel={itemLabel}
            canSubmit={canSubmit}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        )}
      />
      {errorMessage ? (
        <Text color="red.500" fontSize="sm" m={0}>
          {errorMessage}
        </Text>
      ) : null}
    </VStack>
  )
}

function AugmentationPanelSectionAddButton<
  T extends ItemSelectorOption = ItemSelectorOption
>(props: AugmentationPanelSectionAddProps<T>) {
  if ("link" in props) {
    return (
      <AugmentationPanelSectionAddLinkButton {...props} link={props.link} />
    )
  }

  if ("onClick" in props) {
    return (
      <AugmentationPanelSectionAddItem
        itemKey={props.itemKey}
        label={props.label}
        hashKey={props.hashKey}
        onClick={props.onClick}
      />
    )
  }

  return (
    <AugmentationPanelSectionAddSelectButton {...props} select={props.select} />
  )
}

/** Drag affordances for one row, supplied by the sortable list that renders it. */
export type AugmentationPanelItemDragState = {
  dragHandleProps?: SortableListDragHandleProps
  isDragging: boolean
  isDragTarget: boolean
}

/** Stretches a row's primary Link/Button over the pill via a positioned ::after. */
const ROW_PRIMARY_ACTION_CSS = {
  position: "static",
  _after: {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "inherit"
  }
} as const

const DRAG_HANDLE_COLUMN = "20px"
const DRAG_HANDLE_GAP = "2px"
const DRAG_HANDLE_TRANSITION =
  "grid-template-columns 0.2s ease-in-out, column-gap 0.2s ease-in-out"

/** Hovering the sortable items (or an in-flight sort) reveals every row's drag handle. */
const SORTABLE_LIST_DRAG_HANDLE_CSS = {
  [`&:hover .AugmentationPanelSectionItemDragLayout, &[data-sorting] .AugmentationPanelSectionItemDragLayout`]:
    {
      gridTemplateColumns: `${DRAG_HANDLE_COLUMN} minmax(0, 1fr)`,
      columnGap: DRAG_HANDLE_GAP
    },
  [`&:hover .AugmentationPanelSectionItemDragHandle, &[data-sorting] .AugmentationPanelSectionItemDragHandle`]:
    {
      opacity: 1
    },
  "@media (hover: none)": {
    "& .AugmentationPanelSectionItemDragLayout": {
      gridTemplateColumns: "minmax(0, 1fr)",
      columnGap: "0px"
    },
    "& .AugmentationPanelSectionItemDragHandle": {
      display: "none"
    }
  }
} as const

// The order of the controls as they appear in the UI.
const ORDERED_CONTROL_TYPES: AugmentationPanelItemControlType[] = [
  "edit",
  "move-up",
  "move-down",
  "delete"
]

/**
 * Adds the move rows an item can reach from its options menu. Indexes are in
 * sortable-item space — the same space `SortableList` reports a drag in, and
 * the space consumers resolve back to their own ordering.
 */
function resolveItemControlsForSortableList({
  item,
  sortableIndex,
  sortableCount,
  onMove,
  canSort,
  isSortable
}: {
  item: AugmentationPanelItemProps
  sortableIndex: number
  sortableCount: number
  onMove: (moveItem: any, movedIndex: number) => void | Promise<void>
  canSort: boolean
  isSortable: boolean
}): AugmentationPanelItemControl[] {
  const controls = item.controls ? [...item.controls] : []

  if (canSort && isSortable && sortableIndex >= 0) {
    if (sortableIndex > 0) {
      controls.push({
        type: "move-up",
        handler: () => onMove(item, sortableIndex - 1)
      })
    }

    if (sortableIndex < sortableCount - 1) {
      controls.push({
        type: "move-down",
        handler: () => onMove(item, sortableIndex + 1)
      })
    }
  }

  return controls.sort(
    (a, b) =>
      ORDERED_CONTROL_TYPES.indexOf(a.type) -
      ORDERED_CONTROL_TYPES.indexOf(b.type)
  )
}

function defaultGetAddErrorMessage(error: unknown) {
  if (
    error &&
    typeof error === "object" &&
    "graphQLErrors" in error &&
    Array.isArray(error.graphQLErrors)
  ) {
    const graphQLError = error.graphQLErrors.find(
      (value) => value && typeof value.message === "string"
    )

    if (graphQLError?.message) {
      return graphQLError.message
    }
  }

  if (error instanceof Error) {
    return error.message
  }

  return "Unable to save your changes."
}

export function AugmentationPanelSectionList<
  T extends ItemSelectorOption = ItemSelectorOption
>({
  items,
  sortProps,
  addProps,
  hashKey
}: {
  items: AugmentationPanelItemProps[]
  sortProps?: AugmentationPanelSectionListSortProps
  addProps?: AugmentationPanelSectionAddProps<T>
  hashKey?: string
}) {
  const [draggingItemKey, setDraggingItemKey] = useState<string | null>(null)

  const isItemSortable = (item: AugmentationPanelItemProps) =>
    item.sortable !== false && !item.editState

  const listDisabled = Boolean(sortProps?.disabled)
  const sortableCount = items.filter(isItemSortable).length
  const canSort = !!sortProps && sortableCount > 1 && !listDisabled

  if (!sortProps) {
    return (
      <VStack
        align="stretch"
        gap="5px"
        w="full"
        className="AugmentationPanelSectionList"
      >
        {items.map((item) => (
          <AugmentationPanelSectionItem
            key={item.itemKey}
            {...item}
            hashKey={item.hashKey ?? hashKey}
          />
        ))}
        {addProps ? (
          <AugmentationPanelSectionAddButton
            {...addProps}
            hashKey={addProps.hashKey ?? hashKey}
          />
        ) : null}
      </VStack>
    )
  }

  return (
    <VStack
      align="stretch"
      gap="5px"
      w="full"
      className="AugmentationPanelSectionList"
    >
      <Box
        w="full"
        data-sorting={draggingItemKey ? "" : undefined}
        css={canSort ? SORTABLE_LIST_DRAG_HANDLE_CSS : undefined}
      >
        <SortableList
          items={items}
          onChange={sortProps.onSort}
          onDraggingItemKeyChange={setDraggingItemKey}
          disabled={listDisabled}
          animateLayout={sortProps.animateLayout}
          gap="5px"
          renderItem={({
            item,
            index,
            dragHandleProps,
            isDragging,
            isDragTarget
          }) => (
            <AugmentationPanelSectionItem
              {...item}
              isPerformingDrag={!!draggingItemKey}
              // An item can have a hash key set, or it can use its parent's hash key
              hashKey={item.hashKey ?? hashKey}
              dragState={
                canSort && isItemSortable(item)
                  ? {
                      dragHandleProps,
                      isDragging,
                      isDragTarget
                    }
                  : undefined
              }
              controls={resolveItemControlsForSortableList({
                item,
                sortableIndex: index,
                sortableCount,
                onMove: sortProps.onMove,
                canSort,
                isSortable: isItemSortable(item)
              })}
            />
          )}
        />
      </Box>
      {addProps ? (
        <AugmentationPanelSectionAddButton
          {...addProps}
          hashKey={addProps.hashKey ?? hashKey}
        />
      ) : null}
    </VStack>
  )
}

const controlsIconMap: Record<AugmentationPanelItemControl["type"], IconName> =
  {
    edit: "edit",
    ["move-up"]: "chevron-up",
    ["move-down"]: "chevron-down",
    delete: "trash"
  }

function useAugmentationPanelItemControlLabels(): Record<
  AugmentationPanelItemControl["type"],
  string
> {
  const { t } = useTranslate()

  return useMemo(
    () => ({
      edit: t("augmentation_panel_section.controls.edit", {
        defaultValue: "Edit"
      }),
      ["move-up"]: t("augmentation_panel_section.controls.move_up", {
        defaultValue: "Move up"
      }),
      ["move-down"]: t("augmentation_panel_section.controls.move_down", {
        defaultValue: "Move down"
      }),
      delete: t("augmentation_panel_section.controls.delete", {
        defaultValue: "Delete"
      })
    }),
    [t]
  )
}

type ControlContext = {
  itemLabel: string
  itemHashKey?: string
  basePath: string
  setHashKey: (hashKey?: string) => void
}

type ControlButtonProps = {
  "aria-label": string
} & ButtonProps &
  Partial<ReturnType<typeof resolveAugmentationPanelItemLinkProps>>

function getControlProps(
  control: AugmentationPanelItemControl,
  context: ControlContext
): ControlButtonProps {
  const controlHashKey = control.hashKey ?? context.itemHashKey
  const controlType = control.type

  switch (controlType) {
    case "edit": {
      const resolvedLinkProps = resolveAugmentationPanelItemLinkProps({
        ...control,
        basePath: context.basePath
      })

      return {
        ...resolvedLinkProps,
        "aria-label": `Edit ${context.itemLabel}`.trim(),
        onClick: (event) => {
          if (isPrimaryUnmodifiedClick(event)) {
            dispatchAugmentationPanelHashNavigation(control.hashKey)
          }
          control.onClick?.()
        }
      }
    }
    case "delete": {
      return {
        "aria-label": `Delete ${context.itemLabel}`.trim(),
        loading: control.loading,
        disabled: control.disabled || control.loading,
        onClick: () => {
          context.setHashKey(controlHashKey)
          void control.handler()
        }
      }
    }
    case "move-up":
    case "move-down":
      return {
        "aria-label": `Reorder ${context.itemLabel}`.trim(),
        onClick: () => {
          context.setHashKey(controlHashKey)
          void control.handler()
        }
      }
    default: {
      exhaustiveGuard(controlType, "Unknown control type")
    }
  }
}

/** See {@link AugmentationPanelItemLinkTarget} for when to use each prop. */
function resolveAugmentationPanelItemLinkProps({
  to,
  relativeTo,
  href,
  panelRoute,
  hashKey,
  basePath
}: AugmentationPanelItemLinkTarget & {
  basePath: string
}) {
  if (to) return { to: withHashTo(to, hashKey) }
  if (relativeTo)
    return { relativeTo: withHashTo(relativeTo, hashKey) as LinkRelativeTo }
  if (panelRoute) {
    return { to: withUrlHash(path.join(basePath, panelRoute), hashKey) }
  }
  if (href) {
    return {
      href: withUrlHash(href, hashKey),
      target: "_self" as const
    }
  }

  return null
}

const AugmentationPanelSectionItemMenuItem = ({
  icon,
  label,
  isDisabled,
  onClick,
  href,
  relativeTo,
  to
}: {
  icon: IconName
  label: string
  isDisabled: boolean
} & ControlButtonProps) => {
  return (
    <Menu.Item asChild closeOnSelect value={label}>
      <Link
        href={href}
        relativeTo={relativeTo}
        to={to}
        // TODO: fix typing.
        onClick={onClick as unknown as MouseEventHandler<HTMLAnchorElement>}
        css={AUGMENTATION_PANEL_OPTIONS_MENU_ITEM_CSS}
        display="flex"
        minW="8rem"
        aria-disabled={isDisabled || undefined}
        cursor={isDisabled ? "not-allowed" : "pointer"}
        opacity={isDisabled ? 0.5 : undefined}
        pointerEvents={isDisabled ? "none" : undefined}
        variant="plain"
        _hover={{ textDecor: "none" }}
      >
        <HStack
          align="center"
          gap={AUGMENTATION_PANEL_OPTIONS_MENU_ITEM_GAP}
          justify="flex-start"
          w="100%"
        >
          <Pathicon
            icon={icon}
            size={16}
            css={AUGMENTATION_PANEL_MENU_ITEM_ICON_CSS}
          />
          <Text as="span" my={0}>
            {label}
          </Text>
        </HStack>
      </Link>
    </Menu.Item>
  )
}

const AugmentationPanelSectionItemMenuControls = ({
  controls,
  itemLabel,
  itemHashKey
}: {
  controls: AugmentationPanelItemControl[]
  itemLabel: string
  itemHashKey?: string
}) => {
  const { t } = useTranslate()
  const controlsLabelMap = useAugmentationPanelItemControlLabels()
  const setHashKey = useSetHashKey()
  const { url } = useRouteMatch()
  const controlContext = useMemo<ControlContext>(
    () => ({
      itemLabel,
      itemHashKey,
      basePath: url,
      setHashKey
    }),
    [itemHashKey, itemLabel, setHashKey, url]
  )

  return (
    <Box
      className="AugmentationPanelSectionItemControls"
      onPointerDown={(event: PointerEvent<HTMLDivElement>) => {
        event.stopPropagation()
      }}
      onClick={(event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()
      }}
    >
      <Menu.Root
        positioning={{ placement: "right-start" }}
        lazyMount
        unmountOnExit
      >
        {/* The trigger's aria-expanded keeps RevealOnGroupHover open with the menu. */}
        <Menu.Trigger asChild>
          <IconButton
            aria-label={`Options for ${itemLabel}`.trim()}
            size="2xs"
            variant="ghost"
            rounded="full"
          >
            <Pathicon icon="ellipses-horizontal" />
          </IconButton>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              {...AUGMENTATION_PANEL_MENU_CONTENT_PROPS}
              zIndex="popover"
              css={AUGMENTATION_PANEL_TYPOGRAPHY_CSS}
              outline="none"
            >
              {controls.map((control, index) => {
                const icon = controlsIconMap[control.type] ?? "info-circle"
                const label =
                  controlsLabelMap[control.type] ??
                  t("augmentation_panel_section.controls.action", {
                    defaultValue: "Action"
                  })
                const controlProps = getControlProps(control, controlContext)
                const { disabled, loading, ...controlMenuItemProps } =
                  controlProps
                const isDisabled = Boolean(disabled || loading)

                return (
                  <AugmentationPanelSectionItemMenuItem
                    key={`${control.type}-${index}`}
                    icon={icon}
                    label={label}
                    isDisabled={isDisabled}
                    {...controlMenuItemProps}
                  />
                )
              })}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </Box>
  )
}

const AugmentationPanelSectionItemControls = ({
  controls,
  itemLabel,
  itemHashKey,
  isPerformingDrag,
  isDragging
}: {
  controls: AugmentationPanelItemControl[]
  itemLabel: string
  itemHashKey?: string
  isPerformingDrag?: boolean
  isDragging?: boolean
}) => {
  // Desktop reorders via a dedicated drag handle; move up/down live in the
  // options menu on every device. Every row hides its menu mid-drag,
  // including the one being dragged.
  if (isPerformingDrag || isDragging || !controls.length) return null

  return (
    <HStack
      className="AugmentationPanelSectionItemControls"
      gap={0}
      position="relative"
      zIndex={1}
    >
      {/* Reveals the menu on row hover, and keeps it while the menu is open
          (aria-expanded) or when the device has no hover at all. */}
      <RevealOnGroupHover>
        <AugmentationPanelSectionItemMenuControls
          controls={controls}
          itemLabel={itemLabel}
          itemHashKey={itemHashKey}
        />
      </RevealOnGroupHover>
    </HStack>
  )
}

type AugmentationPanelSectionItemInternalProps = AugmentationPanelItemProps & {
  /** Supplied by `AugmentationPanelSectionList` when the row can be dragged. */
  dragState?: AugmentationPanelItemDragState
}

const AugmentationPanelSectionItem = ({
  isPerformingDrag,
  icon,
  leading,
  label,
  to,
  href,
  panelRoute,
  onClick,
  onDoubleActivate,
  hashKey,
  controls,
  tone,
  editState,
  dragState
}: AugmentationPanelSectionItemInternalProps) => {
  const itemControls = controls || []
  const isInteractive = Boolean(
    to ||
      href ||
      panelRoute ||
      onClick ||
      onDoubleActivate ||
      itemControls.length ||
      editState
  )
  const isDragging = dragState?.isDragging ?? false
  const isDragTarget = dragState?.isDragTarget ?? false
  const dragHandleProps =
    dragState?.dragHandleProps && !editState
      ? dragState.dragHandleProps
      : undefined
  const showDragHandle = Boolean(dragHandleProps)
  // Dragging now uses a separate handle, so sortable rows can keep a full-row
  // primary action. Double-activation still needs a non-interactive background.
  const primaryActionCss = onDoubleActivate ? undefined : ROW_PRIMARY_ACTION_CSS
  const { url } = useRouteMatch()
  const linkContent = (
    <Text as="span" textStyle="augmentationPanel" lineClamp={1}>
      {label}
    </Text>
  )
  const leadingContent = editState?.leading ?? leading ?? (
    <Pathicon icon={icon ?? "info-circle"} />
  )
  const usesDefaultIcon = !editState?.leading && !leading

  const linkProps = useMemo(
    () =>
      resolveAugmentationPanelItemLinkProps({
        to,
        href,
        panelRoute,
        hashKey,
        basePath: url
      }) ?? {},
    [hashKey, href, panelRoute, to, url]
  )

  const setHashKey = useSetHashKey()
  const doubleActivateProps = useDoubleActivate<HTMLDivElement>({
    onDoubleActivate,
    excludeTargetSelector:
      ".AugmentationPanelSectionItemControls, .AugmentationPanelSectionItemDragHandle"
  })

  const handleEditKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!editState) return

    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      editState.onCancel()
    } else if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      // Multiline prop/JSON/code editors need Enter for new lines, not row save.
      if (
        event.target instanceof Element &&
        event.target.closest("textarea, [contenteditable=true]")
      )
        return
      event.preventDefault()
      if (editState.canSave !== false) editState.onSave()
    }
  }

  const itemClassName =
    tone === "muted"
      ? "AugmentationPanelSectionItem AugmentationPanelSectionAddButton"
      : "AugmentationPanelSectionItem"

  const row = (
    <AugmentationPanelRow
      className={showDragHandle ? itemClassName : `${itemClassName} group`}
      interactive={isInteractive}
      position="relative"
      opacity={isDragging ? 0.72 : 1}
      borderWidth={isDragTarget ? "1px" : "0px"}
      borderColor={isDragTarget ? "blackAlpha.300" : "transparent"}
      bg={isDragging || isDragTarget ? ITEM_FILL_BG : undefined}
      transition={`background-color 0.15s ease, border-color 0.15s ease, opacity 0.2s ease, box-shadow ${AUGMENTATION_SECTION_FOCUS_OUT_MS}ms ease`}
      touchAction={onDoubleActivate ? "manipulation" : undefined}
      onKeyDown={editState ? handleEditKeyDown : undefined}
      {...doubleActivateProps}
    >
      <HStack
        align={editState ? "stretch" : "center"}
        gap="augmentationPanel"
        minW={0}
        w="full"
      >
        <Box
          position="relative"
          zIndex={1}
          w={editState ? "auto" : "augmentationPanelIcon"}
          minW={editState ? "min-content" : "augmentationPanelIcon"}
          h={editState ? "auto" : "augmentationPanelIcon"}
          alignSelf={editState ? "stretch" : undefined}
          flex="0 0 auto"
          display="inline-flex"
          alignItems={editState ? "stretch" : "center"}
          justifyContent="center"
          opacity={usesDefaultIcon ? 0.8 : 1}
          pointerEvents="none"
          css={{
            "& :where(a, button, input, textarea, select, [role=button])": {
              pointerEvents: "auto"
            }
          }}
        >
          {leadingContent}
        </Box>
        {editState ? (
          editState.content
        ) : to || href || panelRoute ? (
          <Link
            {...linkProps}
            color="fg"
            textDecoration="none"
            variant="plain"
            css={primaryActionCss}
            _hover={{ textDecoration: "none" }}
            onClick={(event) => {
              if (isPrimaryUnmodifiedClick(event)) {
                dispatchAugmentationPanelHashNavigation(hashKey)
              }
            }}
          >
            {linkContent}
          </Link>
        ) : onClick ? (
          <Button
            variant="plain"
            color="fg"
            h="auto"
            minH={0}
            minW={0}
            p={0}
            fontFamily="inherit"
            fontSize="inherit"
            fontWeight="inherit"
            lineHeight="inherit"
            justifyContent="flex-start"
            textAlign="left"
            textDecoration="none"
            css={primaryActionCss}
            _hover={{ textDecoration: "none" }}
            onClick={() => {
              setHashKey(hashKey)
              onClick()
            }}
          >
            {linkContent}
          </Button>
        ) : (
          linkContent
        )}
      </HStack>
      {editState ? (
        <HStack
          className="AugmentationPanelSectionItemControls"
          gap={1}
          position="relative"
          zIndex={1}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <IconButton
            aria-label={`Cancel editing ${label || "scale point"}`}
            size="2xs"
            variant="ghost"
            rounded="full"
            disabled={editState.isSaving}
            onClick={editState.onCancel}
          >
            <Pathicon icon="x" />
          </IconButton>
          <IconButton
            aria-label={`Save ${label || "scale point"}`}
            size="2xs"
            variant="ghost"
            rounded="full"
            loading={editState.isSaving}
            disabled={editState.canSave === false || editState.isSaving}
            onClick={editState.onSave}
          >
            <Pathicon icon="check" />
          </IconButton>
        </HStack>
      ) : itemControls.length ? (
        <AugmentationPanelSectionItemControls
          isPerformingDrag={isPerformingDrag}
          isDragging={isDragging}
          controls={itemControls}
          itemLabel={label}
          itemHashKey={hashKey}
        />
      ) : null}
    </AugmentationPanelRow>
  )

  if (!showDragHandle || !dragHandleProps) {
    return row
  }

  return (
    <Box
      className="AugmentationPanelSectionItemDragLayout group"
      display="grid"
      w="full"
      alignItems="center"
      gridTemplateColumns={`0px minmax(0, 1fr)`}
      columnGap="0px"
      transition={DRAG_HANDLE_TRANSITION}
    >
      <Box
        className="AugmentationPanelSectionItemDragHandle"
        role="button"
        tabIndex={-1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        minW={0}
        h="full"
        cursor="grab"
        opacity={0}
        transition="opacity 0.2s ease-in-out"
        color="augmentationPanel.text"
        aria-label={`Reorder ${label}`.trim()}
        touchAction="none"
        onPointerDown={dragHandleProps.onPointerDown}
        _active={{ cursor: "grabbing" }}
      >
        <Pathicon icon="drag" size={16} />
      </Box>
      {row}
    </Box>
  )
}

/**
 * The parts are also exported individually — import them by name when you only
 * need one, or take the namespace for the `AugmentationPanelSection.Root` form.
 */
export default {
  Container: AugmentationPanelSectionContainer,
  Content: AugmentationPanelSectionContent,
  Divider: AugmentationPanelSectionDivider,
  Group: AugmentationPanelSectionGroup,
  Heading: AugmentationPanelSectionHeading,
  List: AugmentationPanelSectionList,
  Root: AugmentationPanelSectionRoot
}
